import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import type { Moment } from 'moment';
import {
    CalendarOutlined,
    CaretDownOutlined,
} from '@ant-design/icons';

const { RangePicker } = DatePicker;

// @@ AI-COMMENT: Quick-select preset definitions (Kibana-style)
const PRESETS: { label: string; getDates: () => [Moment, Moment] }[] = [
    { label: 'Today', getDates: () => [moment().startOf('day'), moment().endOf('day')] },
    { label: 'Yesterday', getDates: () => [moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day')] },
    { label: 'Last 7 days', getDates: () => [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')] },
    { label: 'Last 14 days', getDates: () => [moment().subtract(13, 'days').startOf('day'), moment().endOf('day')] },
    { label: 'Last 30 days', getDates: () => [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')] },
    { label: 'Last 90 days', getDates: () => [moment().subtract(89, 'days').startOf('day'), moment().endOf('day')] },
    { label: 'This week', getDates: () => [moment().startOf('isoWeek'), moment().endOf('isoWeek')] },
    { label: 'This month', getDates: () => [moment().startOf('month'), moment().endOf('month')] },
];

export interface DateRangePickerProps {
    /**
     * Current selected date range
     */
    dateRange: [Moment, Moment];
    /**
     * Callback when date range is applied/changed
     */
    onApply: (range: [Moment, Moment]) => void;
    /**
     * Custom button class name
     */
    className?: string;
    /**
     * Custom presets to override default ones
     */
    presets?: { label: string; getDates: () => [Moment, Moment] }[];
    /**
     * Disable future dates
     */
    disableFuture?: boolean;
    /**
     * Custom button style
     */
    buttonStyle?: React.CSSProperties;
    /**
     * Custom button class name (overrides default buttonBaseClass if provided)
     */
    buttonClassName?: string;
    /**
     * Show button with minimal styling
     */
    minimal?: boolean;
}

// @@ AI-COMMENT: Reusable Kibana-style date range picker dropdown component
const DateRangePicker: React.FC<DateRangePickerProps> = ({
    dateRange,
    onApply,
    className = '',
    presets = PRESETS,
    disableFuture = false,
    buttonStyle = {},
    buttonClassName = '',
    minimal = false
}) => {
    const [open, setOpen] = useState(false);
    // @@ AI-COMMENT: RangePicker uses dayjs internally (Ant Design 5.x requirement)
    const toDayjs = (m: Moment): Dayjs => dayjs(m.toISOString());
    const [tempRange, setTempRange] = useState<[Dayjs | null, Dayjs | null]>([toDayjs(dateRange[0]), toDayjs(dateRange[1])]);
    const ref = useRef<HTMLDivElement>(null);

    // @@ AI-COMMENT: Update temp range when prop changes
    useEffect(() => {
        setTempRange([toDayjs(dateRange[0]), toDayjs(dateRange[1])]);
    }, [dateRange]);

    // @@ AI-COMMENT: Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handlePreset = (preset: typeof presets[number]) => {
        const range = preset.getDates();
        setTempRange([toDayjs(range[0]), toDayjs(range[1])]);
        onApply(range);
        setOpen(false);
    };

    const handleApply = () => {
        // Only apply if both dates are selected and valid
        if (tempRange && tempRange[0] && tempRange[1]) {
            console.log('Applying dates:', tempRange);
            onApply([moment(tempRange[0].toISOString()), moment(tempRange[1].toISOString())]);
            setOpen(false);
        }
    };

    const handleCancel = () => {
        // Reset to current dateRange
        setTempRange([toDayjs(dateRange[0]), toDayjs(dateRange[1])]);
        setOpen(false);
    };

    // @@ AI-COMMENT: Display labels derived from tempRange (dayjs) with fallback for missing dates
    const fromLabel = tempRange && tempRange[0] ? tempRange[0].format('ddd, DD MMM YYYY') : 'Select start date';
    const toLabel = tempRange && tempRange[1] ? tempRange[1].format('ddd, DD MMM YYYY') : 'Select end date';

    const buttonBaseClass = buttonClassName || (minimal
        ? "flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        : "flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors shadow-sm font-medium");

    return (
        <div ref={ref} className={`relative ${className}`}>
            {/* @@ AI-COMMENT: Trigger button showing current range */}
            <button
                onClick={() => setOpen(o => !o)}
                className={buttonBaseClass}
                style={buttonStyle}
                type="button"
            >
                <CalendarOutlined className={`${minimal ? 'text-gray-400' : 'text-blue-500'}`} />
                <span>{dateRange[0].format('DD MMM YYYY')} — {dateRange[1].format('DD MMM YYYY')}</span>
                <CaretDownOutlined className={`text-xs ${minimal ? 'text-gray-400' : 'text-gray-400'} transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {/* @@ AI-COMMENT: Kibana-style dropdown panel */}
            {open && (
                <div
                    className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl flex overflow-hidden"
                    style={{ width: 580, minHeight: 340 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* @@ AI-COMMENT: Quick select presets sidebar */}
                    <div className="w-44 border-r border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 p-3">
                        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Quick select</p>
                        {presets.map(p => (
                            <button
                                key={p.label}
                                onClick={() => handlePreset(p)}
                                className="w-full text-left px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg font-medium transition-colors"
                                type="button"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {/* @@ AI-COMMENT: Custom date range picker panel */}
                    <div className="flex-1 p-4 flex flex-col justify-between" onClick={(e) => e.stopPropagation()}>
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-3">Custom range</p>
                            <RangePicker
                                value={tempRange}
                                getPopupContainer={() => ref.current!}
                                onChange={(dates) => {
                                    console.log('DateRangePicker onChange:', dates);
                                    if (dates) {
                                        setTempRange([dates[0], dates[1]]);
                                    } else {
                                        setTempRange([null, null]);
                                    }
                                }}
                                format="DD MMM YYYY"
                                style={{ width: '100%' }}
                                allowClear={true}
                                disabledDate={disableFuture ? (current) => current && current.isAfter(dayjs(), 'day') : undefined}
                                placeholder={['Start Date', 'End Date']}
                                showTime={false}
                            />
                            <div className="mt-3 grid grid-cols-2 gap-3 text-[10px] text-gray-500 dark:text-gray-400">
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                                    <p className="font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">From</p>
                                    <p className="text-gray-700 dark:text-gray-300 font-medium">{fromLabel}</p>
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                                    <p className="font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">To</p>
                                    <p className="text-gray-700 dark:text-gray-300 font-medium">{toLabel}</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={handleCancel}
                                className="px-4 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApply}
                                disabled={!tempRange || !tempRange[0] || !tempRange[1]}
                                className={`px-4 py-1.5 text-xs rounded-lg font-semibold transition-colors ${tempRange && tempRange[0] && tempRange[1]
                                    ? 'text-white bg-blue-600 hover:bg-blue-700'
                                    : 'text-gray-400 bg-gray-200 cursor-not-allowed'
                                    }`}
                                type="button"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateRangePicker;